import React, { useState } from 'react';
import { PublicLayout } from '../../components/layout/PublicLayout';
import { Heart, Briefcase, GraduationCap, Gift } from '@phosphor-icons/react';
import { Input } from '../../components/ui/input';
import { Textarea } from '../../components/ui/textarea';
import { Button } from '../../components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { toast } from 'sonner';
import { inquiriesAPI } from '../../lib/api';

const supportCategories = [
  { icon: Heart, title: 'General Donations', desc: 'Support the overall mission and operations of the Order.' },
  { icon: Briefcase, title: 'Project Sponsorship', desc: 'Fund specific charitable projects and initiatives.' },
  { icon: GraduationCap, title: 'Education Fund', desc: 'Support educational and training programs.' },
  { icon: Gift, title: 'Event Sponsorship', desc: 'Sponsor ceremonies, gatherings, and special events.' },
];

const SupportPage = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    amount: '',
    purpose: 'general',
    message: ''
  });
  const [submitting, setSubmitting] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      await inquiriesAPI.create({
        name: formData.name,
        email: formData.email,
        subject: `Contribution Interest - ${formData.purpose} - ${formData.amount ? '$' + formData.amount : 'Amount TBD'}`,
        message: formData.message || `Interest in contributing to: ${formData.purpose}`,
        inquiry_type: 'contribution_interest'
      });
      toast.success('Thank you for your interest in supporting the Order. We will contact you shortly.');
      setFormData({ name: '', email: '', amount: '', purpose: 'general', message: '' });
    } catch (error) {
      toast.error('Failed to submit. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  return (
    <PublicLayout>
      {/* Header */}
      <section className="py-20 bg-[#1A1614] text-center" data-testid="support-header">
        <p className="text-overline mb-4">Charitable Works</p>
        <h1 className="font-heading text-4xl sm:text-5xl text-white mb-4">
          Support & Contributions
        </h1>
        <p className="text-[#8A847A] max-w-2xl mx-auto px-4">
          Support the charitable mission of the Grand Priory of the Caribbean
        </p>
      </section>

      {/* Mission Statement */}
      <section className="py-16 bg-white" data-testid="support-mission">
        <div className="max-w-4xl mx-auto px-4 text-center">
          <h2 className="font-heading text-3xl text-[#1A1614] mb-6">Our Charitable Mission</h2>
          <p className="text-[#4A453E] leading-relaxed">
            The Grand Priory of the Caribbean is committed to charitable works throughout 
            the region. Your voluntary contributions support humanitarian initiatives, 
            educational programs, and the preservation of our chivalric heritage. All 
            contributions are received with gratitude and used in accordance with the 
            Order's charitable objectives.
          </p>
        </div>
      </section>

      {/* Support Categories */}
      <section className="py-16 bg-[#F5F2EB]" data-testid="support-categories">
        <div className="max-w-6xl mx-auto px-4">
          <h2 className="font-heading text-3xl text-[#1A1614] mb-8 text-center">Ways to Contribute</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {supportCategories.map(category => {
              const Icon = category.icon;
              return (
                <div 
                  key={category.title}
                  className="bg-white border border-[#D8D2C4] p-6 text-center card-hover"
                  data-testid={`support-category-${category.title.toLowerCase().replace(/\s+/g, '-')}`}
                >
                  <div className="w-16 h-16 mx-auto mb-4 flex items-center justify-center bg-[#7B1818]/10 text-[#7B1818]">
                    <Icon size={32} weight="regular" />
                  </div>
                  <h3 className="font-heading text-lg text-[#1A1614] mb-2">{category.title}</h3>
                  <p className="text-sm text-[#4A453E]">{category.desc}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Contribution Form */}
      <section className="py-16 bg-white" data-testid="support-form-section">
        <div className="max-w-2xl mx-auto px-4">
          <h2 className="font-heading text-3xl text-[#1A1614] mb-8 text-center">
            Express Interest in Contributing
          </h2>
          <p className="text-center text-[#4A453E] mb-8">
            Please fill out the form below to express your interest in supporting the Order. 
            A member of our team will contact you to discuss contribution options.
          </p>

          <form onSubmit={handleSubmit} className="space-y-6 bg-[#F5F2EB] border border-[#D8D2C4] p-8" data-testid="contribution-form">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-[#1A1614] mb-2">Your Name *</label>
                <Input
                  name="name"
                  value={formData.name}
                  onChange={handleChange}
                  required
                  className="rounded-none border-[#D8D2C4] focus:border-[#C5A059] bg-white"
                  data-testid="support-input-name"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-[#1A1614] mb-2">Email Address *</label>
                <Input
                  name="email"
                  type="email"
                  value={formData.email}
                  onChange={handleChange}
                  required
                  className="rounded-none border-[#D8D2C4] focus:border-[#C5A059] bg-white"
                  data-testid="support-input-email"
                />
              </div>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-[#1A1614] mb-2">Intended Amount (USD)</label>
                <Input
                  name="amount"
                  type="number"
                  value={formData.amount}
                  onChange={handleChange}
                  placeholder="Optional"
                  className="rounded-none border-[#D8D2C4] focus:border-[#C5A059] bg-white"
                  data-testid="support-input-amount"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-[#1A1614] mb-2">Purpose *</label>
                <Select 
                  value={formData.purpose} 
                  onValueChange={(value) => setFormData({ ...formData, purpose: value })}
                >
                  <SelectTrigger className="rounded-none border-[#D8D2C4] focus:border-[#C5A059] bg-white" data-testid="support-select-purpose">
                    <SelectValue placeholder="Select purpose" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="general">General Donation</SelectItem>
                    <SelectItem value="project">Project Sponsorship</SelectItem>
                    <SelectItem value="education">Education Fund</SelectItem>
                    <SelectItem value="event">Event Sponsorship</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div>
              <label className="block text-sm font-medium text-[#1A1614] mb-2">Message (Optional)</label>
              <Textarea
                name="message"
                value={formData.message}
                onChange={handleChange}
                rows={4}
                placeholder="Any additional information or questions"
                className="rounded-none border-[#D8D2C4] focus:border-[#C5A059] bg-white"
                data-testid="support-input-message"
              />
            </div>
            <div className="text-center">
              <Button
                type="submit"
                disabled={submitting}
                className="px-8 py-3 bg-[#7B1818] text-white hover:bg-[#5C1212] rounded-none"
                data-testid="support-submit-button"
              >
                {submitting ? 'Submitting...' : 'Submit Interest'}
              </Button>
            </div>
          </form>
        </div>
      </section>

      {/* Note */}
      <section className="py-8 bg-[#F5F2EB]">
        <div className="max-w-2xl mx-auto px-4 text-center">
          <p className="text-sm text-[#8A847A]">
            Note: The Grand Priory does not collect membership dues. All contributions are 
            voluntary and are used solely for charitable purposes in accordance with the 
            Order's mission.
          </p>
        </div>
      </section>
    </PublicLayout>
  );
};

export default SupportPage;
